import { useState, useEffect } from "react";
import axios from "axios";
import baseUrl from '../api/utils';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const LicenceUpload = () => {
    const MySwal = withReactContent(Swal);
    const token = sessionStorage.getItem("token");
    const role = sessionStorage.getItem("role");

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    const fetchStatus = async () => {
        try {
            const response = await axios.get(`${baseUrl}/api/v2/license/status`, {
                headers: { Authorization: token },
            });
            setStatus(response.data);
        } catch (error) {
            console.error("Error fetching license status:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
    }, []);

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setSelectedFile(e.dataTransfer.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            MySwal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: 'Please select a license file first!',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });
            return;
        }

        const formData = new FormData();
        formData.append("licenseFile", selectedFile);

        setUploading(true);
        try {
            await axios.post(`${baseUrl}/api/v2/license/upload`, formData, {
                headers: {
                    Authorization: token,
                    "Content-Type": "multipart/form-data",
                },
            });

            MySwal.fire({
                icon: 'success',
                title: 'License Uploaded',
                text: 'Your license has been activated successfully.',
                confirmButtonText: 'OK',
            }).then(() => {
                setSelectedFile(null);
                fetchStatus();
            });
        } catch (error) {
            const message = error.response?.data || "Failed to upload license file.";
            MySwal.fire({
                icon: 'error',
                title: 'Upload Failed',
                text: typeof message === "string" ? message : "Failed to upload license file.",
                confirmButtonText: 'OK',
            });
        } finally {
            setUploading(false);
        }
    };

    // ── STATUS HELPERS ─────────────────────────────────────────
    const daysColor = (days) => {
        if (days === undefined || days === null) return "#888";
        if (days < 0) return "#e74c3c";
        if (days <= 7) return "#f39c12";
        return "#27ae60";
    };

    const daysBg = (days) => {
        if (days === undefined || days === null) return "#f4f4f4";
        if (days < 0) return "#fff0f0";
        if (days <= 7) return "#fff8e6";
        return "#e6f9f0";
    };

    const progressPct = (days) => {
        if (days === undefined || days === null) return 0;
        return Math.max(0, Math.min(100, (days / 365) * 100));
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        try {
            const d = new Date(dateStr);
            if (isNaN(d)) return dateStr;
            return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
        } catch {
            return dateStr;
        }
    };

    // ── Small stat item (icon chip + label + value) ── mirrors reference UI
    const StatItem = ({ icon, iconBg, iconColor, label, value }) => (
        <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            <div
                style={{
                    width: "42px", height: "42px", borderRadius: "50%",
                    background: iconBg, display: "flex",
                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
            >
                <i className={icon} style={{ color: iconColor, fontSize: "16px" }}></i>
            </div>
            <div>
                <p style={{ fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "2px" }}>
                    {label}
                </p>
                <div style={{ fontSize: "15px", fontWeight: "700", color: "#2b2b2b" }}>
                    {value}
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="invoice-card-wrapper" style={{ marginTop: "-115px" }}>
                <div>
                    <div className="card invoice-card">
                        <div className="card-body text-center py-5">
                            <div className="spinner-border text-danger"></div>
                            <p className="mt-3" style={{ fontSize: "13px", color: "#888" }}>
                                Loading license status...
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="invoice-card-wrapper" style={{ marginTop: "-115px" }}>
            <div>
                <div className="card invoice-card">
                    <div className="card-body p-0">

                        {/* ── HEADER ── */}
                        <div
                            className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom"
                            style={{ gap: "12px", flexWrap: "wrap" }}
                        >
                            <h5 className="mb-0 font-weight-bold" style={{ fontSize: "18px", color: "#1c1c1c" }}>
                                License Status
                            </h5>

                            {status?.exists && (
                                <span
                                    style={{
                                        background: status.valid ? "#e6f9f0" : "#fff0f0",
                                        color: status.valid ? "#27ae60" : "#e74c3c",
                                        padding: "4px 14px", borderRadius: "20px",
                                        fontWeight: "600", fontSize: "12px",
                                    }}
                                >
                                    ● {status.valid ? "Active" : "Expired"}
                                </span>
                            )}
                        </div>

                        <div className="p-4">
                            {!status || !status.exists ? (
                                <div
                                    className="d-flex flex-column align-items-center justify-content-center"
                                    style={{ paddingTop: "10px", paddingBottom: "20px" }}
                                >
                                    <div
                                        style={{
                                            width: "80px", height: "80px", background: "#fff0f0",
                                            borderRadius: "50%", display: "flex", alignItems: "center",
                                            justifyContent: "center", marginBottom: "14px",
                                            boxShadow: "0 4px 16px rgba(231,76,60,0.10)",
                                        }}
                                    >
                                        <i className="fa-solid fa-file-circle-exclamation" style={{ fontSize: "32px", color: "#e74c3c" }}></i>
                                    </div>
                                    <p style={{ fontSize: "15px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                                        No license uploaded yet
                                    </p>
                                    <p style={{ fontSize: "13px", color: "#aaa", textAlign: "center", maxWidth: "320px" }}>
                                        Upload a license file below to activate your company account.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* ── STAT ROW (icon-chip style, like reference) ── */}
                                    <div
                                        style={{
                                            border: "1px solid #edf0f4",
                                            borderRadius: "12px",
                                            padding: "20px 24px",
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                            gap: "20px",
                                            marginBottom: "20px",
                                        }}
                                    >
                                        <StatItem
                                            icon="fa-solid fa-building"
                                            iconBg="#eaf3ff"
                                            iconColor="#2f80ed"
                                            label="Company"
                                            value={status.companyName}
                                        />
                                        <StatItem
                                            icon="fa-solid fa-box"
                                            iconBg="#eaf3ff"
                                            iconColor="#2f80ed"
                                            label="Product"
                                            value={status.productname}
                                        />
                                        <StatItem
                                            icon="fa-solid fa-crown"
                                            iconBg="#eaf3ff"
                                            iconColor="gold"
                                            label="Plan"
                                            value={
                                                <span
                                                    style={{
                                                        background: "#eaf3ff", color: "gold",
                                                        padding: "2px 10px", borderRadius: "20px",
                                                        fontWeight: "700", fontSize: "13px",
                                                    }}
                                                >
                                                    {status.licenseType}
                                                </span>
                                            }
                                        />
                                        <StatItem
                                            icon="fa-solid fa-users"
                                            iconBg="#eaf3ff"
                                            iconColor="#2f80ed"
                                            label="Users"
                                            value={<>{status.usersUsed} <span style={{ color: "#aaa", fontWeight: "500" }}>/ {status.maxUsers ?? "Unlimited"}</span></>}
                                        />
                                        <StatItem
                                            icon="fa-solid fa-calendar-days"
                                            iconBg="#eaf3ff"
                                            iconColor="#2f80ed"
                                            label="Valid Till"
                                            value={formatDate(status.endDate)}
                                        />
                                    </div>

                                    {/* ── VALIDITY / PROGRESS ── (no duplicate expiry box) */}
                                    <div
                                        style={{
                                            border: "1px solid #edf0f4",
                                            borderRadius: "12px",
                                            padding: "20px 24px",
                                        }}
                                    >
                                        <div className="d-flex align-items-center" style={{ gap: "16px", marginBottom: "14px" }}>
                                            <div
                                                style={{
                                                    width: "48px", height: "48px", borderRadius: "50%",
                                                    background: daysBg(status.daysRemaining),
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <i className="fa-solid fa-calendar-check" style={{ color: daysColor(status.daysRemaining), fontSize: "18px" }}></i>
                                            </div>
                                            <div>
                                                <p style={{ fontSize: "11px", color: "#999", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "2px" }}>
                                                    License Validity
                                                </p>
                                                <div>
                                                    <span style={{ fontSize: "20px", fontWeight: "800", color: daysColor(status.daysRemaining) }}>
                                                        {status.daysRemaining}
                                                    </span>{" "}
                                                    <span style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
                                                        days remaining
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ width: "100%", height: "8px", background: "#eef1f5", borderRadius: "10px", overflow: "hidden" }}>
                                            <div
                                                style={{
                                                    width: `${progressPct(status.daysRemaining)}%`,
                                                    height: "100%",
                                                    background: daysColor(status.daysRemaining),
                                                    borderRadius: "10px",
                                                    transition: "width 0.4s ease",
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* ── UPLOAD SECTION ── */}
                        <div className="px-4 pb-4">
                            {role === "ADMIN" ? (
                                <div style={{ border: "1px solid #edf0f4", borderRadius: "12px", overflow: "hidden" }}>
                                    <div className="d-flex align-items-center px-4 pt-3 pb-2" style={{ gap: "12px" }}>
                                        <div
                                            style={{
                                                width: "38px", height: "38px", borderRadius: "50%",
                                                background: "#eaf3ff", display: "flex",
                                                alignItems: "center", justifyContent: "center", flexShrink: 0,
                                            }}
                                        >
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ color: "#2f80ed", fontSize: "16px" }}></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 font-weight-bold" style={{ fontSize: "15px", color: "#1c1c1c" }}>
                                                Upload License File
                                            </h6>
                                            <p style={{ fontSize: "12px", color: "#999", marginBottom: 0 }}>
                                                Upload your license file to activate or update your software.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="row no-gutters px-4 pb-4" style={{ gap: "0" }}>
                                        <div className="col-md-8" style={{ paddingRight: "20px" }}>
                                            <label
                                                htmlFor="licenseFileInput"
                                                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                                                onDragLeave={() => setDragActive(false)}
                                                onDrop={handleDrop}
                                                style={{
                                                    display: "flex", flexDirection: "column", alignItems: "center",
                                                    justifyContent: "center", textAlign: "center",
                                                    border: `1.5px dashed ${dragActive ? "#2f80ed" : "#d5d9e0"}`,
                                                    borderRadius: "10px", padding: "30px 20px",
                                                    background: dragActive ? "#f2f7ff" : "#fafbfc",
                                                    cursor: "pointer", margin: 0, minHeight: "160px",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: "44px", height: "44px", borderRadius: "50%",
                                                        background: "#eaf3ff", display: "flex",
                                                        alignItems: "center", justifyContent: "center", marginBottom: "10px",
                                                    }}
                                                >
                                                    <i className="fa-solid fa-file-arrow-up" style={{ color: "#2f80ed", fontSize: "18px" }}></i>
                                                </div>
                                                <p style={{ fontSize: "14px", fontWeight: "600", color: "#333", marginBottom: "4px" }}>
                                                    {selectedFile ? selectedFile.name : "Drag & drop your license file here"}
                                                </p>
                                                {!selectedFile && <p style={{ fontSize: "12px", color: "#aaa", marginBottom: "10px" }}>or</p>}
                                                {!selectedFile && (
                                                    <span
                                                        style={{
                                                            background: "#ff5252", color: "#fff",
                                                            padding: "7px 20px", borderRadius: "6px",
                                                            fontWeight: "600", fontSize: "13px",
                                                        }}
                                                    >
                                                        Browse File
                                                    </span>
                                                )}
                                                <p style={{ fontSize: "11px", color: "#bbb", marginTop: "10px", marginBottom: 0 }}>
                                                    Supported format: .xml only
                                                </p>
                                                <input
                                                    id="licenseFileInput"
                                                    type="file"
                                                    accept=".xml"
                                                    onChange={handleFileChange}
                                                    style={{ display: "none" }}
                                                />
                                            </label>
                                        </div>

                                        <div
                                            className="col-md-4 d-flex flex-column align-items-center justify-content-center text-center"
                                            style={{ borderLeft: "1px solid #edf0f4", padding: "20px" }}
                                        >
                                            <i className="fa-regular fa-file-lines" style={{ fontSize: "26px", color: "#bbb", marginBottom: "10px" }}></i>
                                            <p style={{ fontSize: "12px", color: "#888", marginBottom: 0 }}>
                                                Your current settings and data will be preserved after upload.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end px-4 pb-3">
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleUpload}
                                            disabled={uploading}
                                            style={{
                                                background: "#2f80ed", border: "none",
                                                borderRadius: "8px", fontWeight: "600",
                                                padding: "10px 22px", fontSize: "13px",
                                            }}
                                        >
                                            {uploading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm mr-2"></span>
                                                    Uploading...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa-solid fa-upload mr-2"></i>
                                                    Upload License
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className="d-flex align-items-center"
                                    style={{
                                        gap: "12px", background: "#fff8e6",
                                        border: "1px solid #ffe4a8", borderRadius: "10px",
                                        padding: "14px 16px",
                                    }}
                                >
                                    <i className="fa-solid fa-triangle-exclamation" style={{ color: "#f39c12", fontSize: "18px" }}></i>
                                    <p style={{ fontSize: "13px", color: "#7a5c00", marginBottom: 0 }}>
                                        Your license is invalid or expired. Please contact your Admin to upload a new license file.
                                    </p>
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default LicenceUpload;